import React from 'react';
import { Box, HStack, Icon, Text, Tooltip, useMediaQuery } from '@chakra-ui/react';
import { MinusIcon } from '@chakra-ui/icons';
import { MdAssignment, MdThumbUp } from 'react-icons/md';
import { WorkflowId } from '../contexts/constants';
import i18n from '../utils/i18n';

/** 並列承認ステージ定義（名前切替方式対応） */
interface ParallelStage {
  flowId: string;
  name: string;
  abbr: string;
  requesterName?: string;
  requesterAbbr?: string;
  requiredKey?: "crossCheckRequired" | "materialOfficerRequired";
}

/** parallelApprovalStatus のエントリ型 */
interface StatusEntry {
  flow_id: string;
  completed: string;
  employeeNames?: string[];
  requester?: {
    flow_id: string;
    completed: string;
    employeeNames?: string[];
  };
}

// ステータス"4"（CC・資材担当確認中）: 並列ブロック1
const getParallelStagesStatus4 = (): ParallelStage[] => [
  { flowId: WorkflowId.CrossCheck, name: "CC", abbr: "CC",
    requiredKey: "crossCheckRequired" },
  { flowId: WorkflowId.MaterialOfficer,
    name: i18n.t('common.constants.workflowStage.materialOfficerCheck'),
    abbr: i18n.t('common.constants.workflowStage.materialOfficerCheckAbbr'),
    requesterName: i18n.t('common.constants.workflowStage.materialOfficerCheckRequester'),
    requesterAbbr: i18n.t('common.constants.workflowStage.materialOfficerCheckRequesterAbbr'),
    requiredKey: "materialOfficerRequired" },
];

// ステータス"5"（本部長承認中）: 並列ブロック2
const getParallelStagesStatus5 = (): ParallelStage[] => [
  { flowId: WorkflowId.SalesHQManager,
    name: i18n.t('common.constants.workflowStage.salesHQManager'),
    abbr: i18n.t('common.constants.workflowStage.salesHQManagerAbbr'),
    requesterName: i18n.t('common.constants.workflowStage.salesHQManagerRequester'),
    requesterAbbr: i18n.t('common.constants.workflowStage.salesHQManagerRequesterAbbr') },
  { flowId: WorkflowId.BusinessHQManager,
    name: i18n.t('common.constants.workflowStage.businessHQManager'),
    abbr: i18n.t('common.constants.workflowStage.businessHQManagerAbbr'),
    requesterName: i18n.t('common.constants.workflowStage.businessHQManagerRequester'),
    requesterAbbr: i18n.t('common.constants.workflowStage.businessHQManagerRequesterAbbr') },
];

const ParallelApprovalIcons: React.FC<{
  approvalStatus: string;
  parallelApprovalStatus: string | null | undefined;
  crossCheckRequired?: boolean;
  materialOfficerRequired?: boolean;
}> = ({ approvalStatus, parallelApprovalStatus, crossCheckRequired, materialOfficerRequired }) => {
  const [isWide] = useMediaQuery("(min-width: 1800px)");

  if (!parallelApprovalStatus || (approvalStatus !== "4" && approvalStatus !== "5")) {
    return null;
  }

  let statusList: StatusEntry[] = [];
  try {
    statusList = JSON.parse(parallelApprovalStatus);
  } catch { return null; }

  const stages = approvalStatus === "4" ? getParallelStagesStatus4() : getParallelStagesStatus5();
  const requiredFlags: Record<string, boolean | undefined> = {
    crossCheckRequired,
    materialOfficerRequired,
  };

  return (
    <HStack spacing={1} flexWrap="wrap">
      {stages.map(stage => {
        // 要否チェック（requiredKeyがあるステージのみ）
        if (stage.requiredKey && requiredFlags[stage.requiredKey] === false) {
          // 不要 → グレーアイコン
          return (
            <Tooltip key={stage.flowId} label={`${stage.name}${i18n.t('common.label.notRequired')}`} fontSize="xs">
              <HStack spacing={0.5} opacity={0.5}>
                <Icon as={MinusIcon} color="gray.400" boxSize={3} />
                <Text fontSize="xs" color="gray.400">{isWide ? stage.name : stage.abbr}</Text>
              </HStack>
            </Tooltip>
          );
        }

        const status = statusList.find(s => s.flow_id === stage.flowId);
        const requester = status?.requester;
        const completed = status?.completed === "true";
        const employeeNames = status?.employeeNames || [];

        // 名前切替: 依頼者ありで依頼者未承認 → 依頼者名を表示
        const showRequester = !!requester && requester.completed !== "true";
        const displayName = showRequester
          ? (isWide ? (stage.requesterName || stage.name) : (stage.requesterAbbr || stage.abbr))
          : (isWide ? stage.name : stage.abbr);
        const displayCompleted = showRequester ? false : completed;

        // ツールチップ
        const tooltipContent = requester ? (
          <Box>
            {showRequester ? (
              // 依頼者未承認時: 依頼者の情報のみ
              <>
                <Text fontWeight="bold">{stage.requesterName}</Text>
                {(requester.employeeNames || []).map((name: string, i: number) => (
                  <Text key={`req-${i}`} fontSize="xs">{name}</Text>
                ))}
              </>
            ) : (
              // 依頼者承認後: 依頼者（完了）+ 承認者（状態）
              <>
                <Text fontWeight="bold">{i18n.t('common.label.requester')}</Text>
                {(requester.employeeNames || []).map((name: string, i: number) => (
                  <Text key={`req-${i}`} fontSize="xs">{name}</Text>
                ))}
                <Text fontWeight="bold" mt={1}>{stage.name}</Text>
                {employeeNames.map((name: string, i: number) => (
                  <Text key={i} fontSize="xs">{name}</Text>
                ))}
              </>
            )}
          </Box>
        ) : (
          // 依頼者なし（CC等）: 従来通り
          employeeNames.length > 1
          ? (
            <Box>
              <Text fontWeight="bold">{stage.name}</Text>
              {employeeNames.map((name: string, i: number) => (
                <Text key={i} fontSize="xs">{name}</Text>
              ))}
            </Box>
          )
            : employeeNames[0] || stage.name
        );

        return (
          <Tooltip key={stage.flowId} label={tooltipContent} fontSize="xs">
            <HStack spacing={0.5}>
              <Icon
                as={displayCompleted ? MdThumbUp : MdAssignment}
                color={displayCompleted ? "green.500" : "gray.400"}
                boxSize={3.5}
              />
              <Text fontSize="xs" color={displayCompleted ? undefined : "gray.400"}>
                {displayName}
              </Text>
            </HStack>
          </Tooltip>
        );
      })}
    </HStack>
  );
};

export default ParallelApprovalIcons;
